import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Gift, Hospital, Award, Share2, Droplet, ChevronDown, ChevronUp, Eye, FileText, Download, Filter, Search, TrendingUp, Heart, Users, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../config/api';

const DonationHistory = ({ handleDownloadCertificate }) => {
  const [donationHistory, setDonationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bloodGroupFilter, setBloodGroupFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [expandedDonation, setExpandedDonation] = useState(null);
  const [sharingDonation, setSharingDonation] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [showCertificatePreview, setShowCertificatePreview] = useState(false);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [bloodHeroLogo, setBloodHeroLogo] = useState(null);
  const [bloodDropIcon, setBloodDropIcon] = useState(null);

  // Fetch donation history
  useEffect(() => {
    const fetchDonationHistory = async () => {
      try {
        setLoading(true);
        // Get donor profile which contains donation history
        const response = await api.get('/donors/me');

        // Process both direct donations and accepted requests that were donated
        const directDonations = response.data.donationHistory.map(donation => ({
          donationId: donation._id,
          date: donation.date,
          hospitalName: donation.bloodBank,
          location: donation.location,
          bloodGroup: response.data.bloodType,
          amount: donation.amount,
          status: 'Completed',
          certificate: 'Available',
          type: 'direct',
          rawData: donation
        }));

        // Get completed donations from accepted requests
        const requestDonations = response.data.acceptedRequests
          .filter(req => req.status === 'donated')
          .map(req => ({
            donationId: req._id,
            date: req.donationDate,
            recipientName: req.request?.requester?.name || req.request?.patientName || req.request?.requester?.email?.split('@')[0] || 'Unknown',
            hospitalName: req.request?.hospital?.name || '',
            bloodGroup: response.data.bloodType,
            status: 'Completed',
            certificate: 'Available',
            type: 'request',
            requestId: req.request?._id,
            notes: req.notes,
            donationProofPhoto: req.donationProofPhoto,
            requesterFeedback: req.requesterFeedback,
            requesterDetails: {
              name: req.request?.requester?.name || 'Not available',
              email: req.request?.requester?.email || 'Not available',
              phone: req.request?.requester?.phone || 'Not available',
              relation: req.request?.requesterRelation || ''
            },
            rawData: req
          }));

        // Combine both types of donations and sort by date (newest first)
        const allDonations = [...directDonations, ...requestDonations]
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setDonationHistory(allDonations);

        // Store user information for certificate generation
        setUserInfo({
          name: response.data.name || 'A Generous Donor',
          email: response.data.email,
          phone: response.data.phone,
          bloodType: response.data.bloodType
        });

        // Try to get additional user profile data
        try {
          const profileResponse = await api.get('/auth/detailed-profile');
          if (profileResponse.data.user) {
            setUserInfo(prev => ({
              ...prev,
              name: profileResponse.data.user.name || prev.name,
              email: profileResponse.data.user.email || prev.email,
              phone: profileResponse.data.user.phone || prev.phone
            }));
          }
        } catch (profileError) {
          console.log('Could not fetch detailed profile, using basic user data');
        }

        // Load BloodHero logo
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = () => {
          setBloodHeroLogo(logoImg);
        };
        logoImg.onerror = () => {
          console.log('Failed to load BloodHero logo, using fallback');
        };
        logoImg.src = '/images/cerf_logo.png';

        // Load blood drop icon
        const bloodDropImg = new Image();
        bloodDropImg.crossOrigin = 'anonymous';
        bloodDropImg.onload = () => {
          setBloodDropIcon(bloodDropImg);
        };
        bloodDropImg.onerror = () => {
          console.log('Failed to load blood drop icon, using fallback');
        };
        bloodDropImg.src = '/images/r.png';
      } catch (err) {
        console.error('Error fetching donation history:', err);
        setError('Failed to load donation history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDonationHistory();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';

    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Toggle expanded view for a donation
  const toggleExpand = (donationId) => {
    if (expandedDonation === donationId) {
      setExpandedDonation(null);
    } else {
      setExpandedDonation(donationId);
    }
  };

  // Handle sharing donation details
  const handleShareDetails = (donation) => {
    setSharingDonation(true);

    // Create a shareable text with donation details
    const shareText = `
🩸 Blood Donation Certificate 🩸

Date: ${formatDate(donation.date)}
Blood Group: ${donation.bloodGroup}
Hospital/Blood Bank: ${donation.hospitalName}
${donation.location ? `Location: ${donation.location}` : ''}
${donation.amount ? `Amount: ${donation.amount} ml` : ''}
Status: ${donation.status}

Thank you for being a lifesaver! 
#BloodHero #DonateBloodSaveLife
    `.trim();

    try {
      // Try to use the Web Share API if available
      if (navigator.share) {
        navigator.share({
          title: 'My Blood Donation Certificate',
          text: shareText,
        })
          .then(() => {
            toast.success('Donation details shared successfully!');
          })
          .catch((error) => {
            console.error('Error sharing:', error);
            // Fallback to clipboard if sharing fails
            copyToClipboard(shareText);
          })
          .finally(() => {
            setSharingDonation(false);
          });
      } else {
        // Fallback to clipboard if Web Share API is not available
        copyToClipboard(shareText);
        setSharingDonation(false);
      }
    } catch (error) {
      console.error('Error sharing donation details:', error);
      toast.error('Failed to share donation details');
      setSharingDonation(false);
    }
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Donation details copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy to clipboard');
      });
  };

  // Handle viewing proof of donation in full screen
  const handleViewProof = (imageUrl) => {
    if (!imageUrl) return;

    // Create a modal to view the image
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    modal.style.display = 'flex';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.zIndex = '9999';
    modal.style.cursor = 'pointer';

    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.maxWidth = '90%';
    img.style.maxHeight = '90%';
    img.style.objectFit = 'contain';

    modal.appendChild(img);
    document.body.appendChild(modal);

    // Close modal when clicked
    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
  };

  // Handle downloading proof of donation
  const handleDownloadProof = (imageUrl, donationId) => {
    if (!imageUrl) return;

    // Create a link to download the image
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `donation_proof_${donationId}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Proof of donation downloaded successfully!');
  };

  // Generate certificate preview
  const generateCertificatePreview = (donation) => {
    try {
      // Create canvas for certificate
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size (A4 size in pixels at 96 DPI)
      canvas.width = 794; // A4 width
      canvas.height = 1123; // A4 height

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#fef2f2'); // Light red
      gradient.addColorStop(1, '#fecaca'); // Lighter red
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add decorative border
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 8;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

      // Inner border
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 2;
      ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);

      // Header decoration
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(0, 80, canvas.width, 4);
      ctx.fillRect(0, canvas.height - 80, canvas.width, 4);

      // Draw BloodHero logo centered above title
      if (bloodHeroLogo) {
        ctx.drawImage(bloodHeroLogo, canvas.width / 2 - 100, 80, 200, 100);
      } else {
        // Fallback logo text
        ctx.fillStyle = 'red';
        ctx.font = 'bold 32px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('THE Blood Hero ', canvas.width / 2, 130);
      }

      // Title
      ctx.fillStyle = '#FF0000';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('THE BLOOD HERO ', canvas.width / 2, 200);

      // Subtitle
      ctx.fillStyle = '#6b7280';
      ctx.font = '24px Arial, sans-serif';
      ctx.fillText('Certificate of Appreciation', canvas.width / 2, 240);

      // Blood drop icon
      if (bloodDropIcon) {
        ctx.drawImage(bloodDropIcon, canvas.width / 2 - 40, 260, 80, 80);
      } else {
        // Fallback blood drop icon
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(canvas.width / 2, 300, 30, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 24px Arial, sans-serif';
        ctx.fillText('🩸', canvas.width / 2 - 8, 310);
      }

      // Main content
      ctx.fillStyle = '#374151';
      ctx.font = '20px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('THIS IS TO CERTIFY THAT', canvas.width / 2, 380);

      // Donor name
      ctx.fillStyle = '#dc2626';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText((userInfo?.name || 'A Generous Donor').toUpperCase(), canvas.width / 2, 430);

      ctx.fillStyle = '#374151';
      ctx.font = '20px Arial, sans-serif';
      ctx.fillText('HAS SUCCESSFULLY DONATED BLOOD ON', canvas.width / 2, 470);

      // Donation date
      ctx.fillStyle = '#dc2626';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText(formatDate(donation.date), canvas.width / 2, 510);

      // Blood group information
      ctx.fillStyle = '#374151';
      ctx.font = '18px Arial, sans-serif';
      ctx.fillText(`Blood Group: ${donation.bloodGroup}`, canvas.width / 2, 560);

      if (donation.amount) {
        ctx.fillText(`Amount: ${donation.amount} ml`, canvas.width / 2, 590);
      }

      if (donation.hospitalName) {
        ctx.fillText(`Hospital: ${donation.hospitalName}`, canvas.width / 2, 620);
      }

      // Recipient info for request donations
      if (donation.type === 'request' && donation.recipientName && donation.recipientName !== 'Unknown') {
        ctx.fillText(`Recipient: ${donation.recipientName}`, canvas.width / 2, 680);
      }

      // User contact information
      if (userInfo?.email) {
        ctx.fillText(`Email: ${userInfo.email}`, canvas.width / 2, 710);
      }

      if (userInfo?.phone) {
        ctx.fillText(`Phone: ${userInfo.phone}`, canvas.width / 2, 740);
      }

      // Appreciation message
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText('Thank you for being a lifesaver!', canvas.width / 2, 780);

      ctx.fillStyle = '#6b7280';
      ctx.font = '16px Arial, sans-serif';
      ctx.fillText('Your donation has the power to save up to 3 lives.', canvas.width / 2, 810);
      ctx.fillText('Every drop counts in the fight to save lives.', canvas.width / 2, 840);

      // Certificate number
      ctx.fillStyle = '#9ca3af';
      ctx.font = '14px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`Certificate ID: ${donation.donationId.substring(0, 8).toUpperCase()}`, 60, canvas.height - 120);

      // Date issued
      ctx.textAlign = 'right';
      ctx.fillText(`Issued on: ${new Date().toLocaleDateString()}`, canvas.width - 60, canvas.height - 120);

      ctx.fillStyle = '#6b7280';
      ctx.font = '14px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Empowering lifesavers, one donation at a time', canvas.width / 2, canvas.height - 60);

      // Convert canvas to data URL for preview
      const dataUrl = canvas.toDataURL('image/png');
      setCertificatePreview(dataUrl);
      setShowCertificatePreview(true);

    } catch (error) {
      console.error('Error generating certificate preview:', error);
      toast.error('Failed to generate certificate preview. Please try again.');
    }
  };

  // Generate and download donation certificate
  const generateDonationCertificate = (donation) => {
    setGeneratingCertificate(true);

    try {
      // Create canvas for certificate
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size (A4 size in pixels at 96 DPI)
      canvas.width = 794; // A4 width
      canvas.height = 1123; // A4 height

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#fef2f2'); // Light red
      gradient.addColorStop(1, '#fecaca'); // Lighter red
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add decorative border
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 8;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

      // Inner border
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 2;
      ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);

      // Header decoration
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(0, 80, canvas.width, 4);
      ctx.fillRect(0, canvas.height - 80, canvas.width, 4);

      // Draw BloodHero logo centered above title
      if (bloodHeroLogo) {
        ctx.drawImage(bloodHeroLogo, canvas.width / 2 - 100, 80, 200, 100);
      } else {
        // Fallback logo text
        ctx.fillStyle = 'red';
        ctx.font = 'bold 32px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('THE Blood Hero ', canvas.width / 2, 130);
      }

      // Title
      ctx.fillStyle = '#FF0000';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('THE BLOOD HERO ', canvas.width / 2, 200);

      // Subtitle
      ctx.fillStyle = '#6b7280';
      ctx.font = '24px Arial, sans-serif';
      ctx.fillText('Certificate of Appreciation', canvas.width / 2, 240);

      // Blood drop icon
      if (bloodDropIcon) {
        ctx.drawImage(bloodDropIcon, canvas.width / 2 - 40, 260, 80, 80);
      } else {
        // Fallback blood drop icon
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(canvas.width / 2, 300, 30, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 24px Arial, sans-serif';
        ctx.fillText('🩸', canvas.width / 2 - 8, 310);
      }

      // Main content
      ctx.fillStyle = '#374151';
      ctx.font = '20px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('THIS IS TO CERTIFY THAT', canvas.width / 2, 380);

      // Donor name
      ctx.fillStyle = '#dc2626';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText((userInfo?.name || 'A Generous Donor').toUpperCase(), canvas.width / 2, 430);

      ctx.fillStyle = '#374151';
      ctx.font = '20px Arial, sans-serif';
      ctx.fillText('HAS SUCCESSFULLY DONATED BLOOD ON', canvas.width / 2, 470);

      // Donation date
      ctx.fillStyle = '#dc2626';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText(formatDate(donation.date), canvas.width / 2, 510);

      // Blood group information
      ctx.fillStyle = '#374151';
      ctx.font = '18px Arial, sans-serif';
      ctx.fillText(`Blood Group: ${donation.bloodGroup}`, canvas.width / 2, 560);

      if (donation.amount) {
        ctx.fillText(`Amount: ${donation.amount} ml`, canvas.width / 2, 590);
      }

      if (donation.hospitalName) {
        ctx.fillText(`Hospital: ${donation.hospitalName}`, canvas.width / 2, 620);
      }

      // Recipient info for request donations
      if (donation.type === 'request' && donation.recipientName && donation.recipientName !== 'Unknown') {
        ctx.fillText(`Recipient: ${donation.recipientName}`, canvas.width / 2, 680);
      }

      // User contact information
      if (userInfo?.email) {
        ctx.fillText(`Email: ${userInfo.email}`, canvas.width / 2, 710);
      }

      if (userInfo?.phone) {
        ctx.fillText(`Phone: ${userInfo.phone}`, canvas.width / 2, 740);
      }

      // Appreciation message
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText('Thank you for being a lifesaver!', canvas.width / 2, 780);

      ctx.fillStyle = '#6b7280';
      ctx.font = '16px Arial, sans-serif';
      ctx.fillText('Your donation has the power to save up to 3 lives.', canvas.width / 2, 810);
      ctx.fillText('Every drop counts in the fight to save lives.', canvas.width / 2, 840);

      // Certificate number
      ctx.fillStyle = '#9ca3af';
      ctx.font = '14px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`Certificate ID: ${donation.donationId.substring(0, 8).toUpperCase()}`, 60, canvas.height - 120);

      // Date issued
      ctx.textAlign = 'right';
      ctx.fillText(`Issued on: ${new Date().toLocaleDateString()}`, canvas.width - 60, canvas.height - 120);

      ctx.fillStyle = '#6b7280';
      ctx.font = '14px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Empowering lifesavers, one donation at a time', canvas.width / 2, canvas.height - 60);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `donation_certificate_${donation.donationId.substring(0, 8)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Donation certificate downloaded successfully! 🎉');
      }, 'image/png');

    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Failed to generate certificate. Please try again.');
    } finally {
      setGeneratingCertificate(false);
    }
  };

  // Filter donations based on selected filters
  const filteredDonations = donationHistory.filter(donation => {
    // Filter by blood group
    if (bloodGroupFilter && donation.bloodGroup !== bloodGroupFilter) {
      return false;
    }

    // Filter by time
    if (timeFilter) {
      const donationDate = new Date(donation.date);
      const now = new Date();
      const monthsDiff = (now.getFullYear() - donationDate.getFullYear()) * 12 +
        now.getMonth() - donationDate.getMonth();

      if (timeFilter === 'last_month' && monthsDiff > 1) return false;
      if (timeFilter === 'last_3_months' && monthsDiff > 3) return false;
      if (timeFilter === 'last_6_months' && monthsDiff > 6) return false;
      if (timeFilter === 'last_year' && monthsDiff > 12) return false;
    }

    return true;
  });

  // Calculate statistics
  const totalDonations = filteredDonations.length;
  const totalAmount = filteredDonations.reduce((sum, donation) => sum + (donation.amount || 450), 0);
  const recentDonations = filteredDonations.filter(donation => {
    const donationDate = new Date(donation.date);
    const now = new Date();
    const monthsDiff = (now.getFullYear() - donationDate.getFullYear()) * 12 +
      now.getMonth() - donationDate.getMonth();
    return monthsDiff <= 6;
  }).length;

  return (
    <div className="space-y-8">
      {/* Hero Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-red-50 via-white to-red-50 rounded-2xl shadow-sm border border-red-100">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-pink-500/5"></div>
        <div className="relative p-5">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                    Donation History
                  </h1>
                  <p className="text-gray-600 text-sm mt-0.5">Your journey of saving lives through blood donation</p>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-red-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{totalDonations}</p>
                      <p className="text-xs text-gray-500">Total Donations</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-red-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Droplet className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{totalAmount}ml</p>
                      <p className="text-xs text-gray-500">Blood Donated</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-red-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{recentDonations}</p>
                      <p className="text-xs text-gray-500">Last 6 Months</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <Filter className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Filter Donations</h3>
              <p className="text-xs text-gray-500">Refine your donation history</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">


            <div className="relative">
              <select
                className="pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-xs focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 appearance-none cursor-pointer hover:bg-gray-100"
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
              >
                <option value="">All Time</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="last_year">Last Year</option>
              </select>
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Gift className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading your donation history...</h3>
          <p className="text-gray-500">We're fetching your lifesaving journey</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </motion.div>
      )}

      {/* Donations Grid */}
      {!loading && !error && (
        <div className="space-y-6">
          {filteredDonations.map((donation, index) => (
            <motion.div
              key={donation.donationId}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-red-200"
            >
              <div className="p-5">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left Section - Basic Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Gift className="w-6 h-6 text-white" />
                        </div>
                        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {donation.type === 'direct' ? 'Direct Donation' : 'Request Donation'}
                          </h3>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200">
                            {donation.status}
                          </span>
                        </div>

                        {donation.type === 'request' && donation.recipientName && donation.recipientName !== 'Unknown' && (
                          <p className="text-gray-600 mb-4 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Recipient: <span className="font-medium text-gray-900">{donation.recipientName}</span>
                            {donation.requesterDetails?.relation && (
                              <span className="text-sm text-gray-500">({donation.requesterDetails.relation})</span>
                            )}
                          </p>
                        )}

                        {donation.type === 'direct' && (
                          <p className="text-gray-600 mb-4 flex items-center gap-2">
                            <Hospital className="w-4 h-4" />
                            Direct donation to blood bank
                          </p>
                        )}

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Calendar className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{formatDate(donation.date)}</p>
                              <p className="text-xs text-gray-500">Donation Date</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <Droplet className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{donation.bloodGroup}</p>
                              <p className="text-xs text-gray-500">Blood Group</p>
                            </div>
                          </div>

                          {donation.hospitalName && (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                              <Hospital className="w-5 h-5 text-red-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{donation.hospitalName}</p>
                                <p className="text-xs text-gray-500">Hospital</p>
                              </div>
                            </div>
                          )}

                          {donation.amount && (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                              <Droplet className="w-5 h-5 text-red-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{donation.amount} ml</p>
                                <p className="text-xs text-gray-500">Amount</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Actions */}
                  <div className="flex flex-col gap-3 min-w-[220px] justify-center">
                    {donation.certificate === 'Available' && (
                      <div className="space-y-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => generateCertificatePreview(donation)}
                          className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center gap-3 font-medium shadow-lg hover:shadow-xl"
                        >
                          <Eye className="w-5 h-5" />
                          Preview Certificate
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => generateDonationCertificate(donation)}
                          disabled={generatingCertificate}
                          className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-3 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {generatingCertificate ? (
                            <>
                              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                              Generating...
                            </>
                          ) : (
                            <>
                              <Award className="w-5 h-5" />
                              Download Certificate
                            </>
                          )}
                        </motion.button>
                      </div>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleShareDetails(donation)}
                      disabled={sharingDonation}
                      className="w-full px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:border-red-300 hover:bg-red-50 transition-all duration-200 flex items-center justify-center gap-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Share2 className="w-5 h-5" />
                      {sharingDonation ? 'Sharing...' : 'Share Details'}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggleExpand(donation.donationId)}
                      className="w-full px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:border-red-300 hover:bg-red-50 transition-all duration-200 flex items-center justify-center gap-3 font-medium"
                    >
                      {expandedDonation === donation.donationId ? (
                        <>
                          <ChevronUp className="w-5 h-5" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-5 h-5" />
                          View Details
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>

                {/* Expanded Details Section */}
                {expandedDonation === donation.donationId && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="mt-8 pt-8 border-t border-gray-100"
                  >
                    <h4 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-4 h-4 text-red-600" />
                      </div>
                      Donation Details
                    </h4>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column */}
                      <div className="space-y-6">
                        {/* Certificate Information */}
                        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                          <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-3">
                            <Award className="w-5 h-5 text-red-600" />
                            Certificate Information
                          </h5>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <FileText className="w-6 h-6 text-red-500" />
                              <span className="font-medium text-gray-900">
                                Donation Certificate
                              </span>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${donation.certificate === 'Available'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}>
                              {donation.certificate}
                            </span>
                          </div>
                          {donation.certificate === 'Available' && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => generateDonationCertificate(donation)}
                              disabled={generatingCertificate}
                              className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-3 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {generatingCertificate ? (
                                <>
                                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Award className="w-5 h-5" />
                                  Download Certificate
                                </>
                              )}
                            </motion.button>
                          )}
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-6">
                        {/* Proof of Donation */}
                        {donation.donationProofPhoto && (
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                            <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-3">
                              <Eye className="w-5 h-5 text-blue-600" />
                              Proof of Donation
                            </h5>
                            <div className="flex gap-3">
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleViewProof(donation.donationProofPhoto)}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <Eye className="w-4 h-4" />
                                View Image
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleDownloadProof(donation.donationProofPhoto, donation.donationId)}
                                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </motion.button>
                            </div>
                          </div>
                        )}

                        {/* Requester Details (for request-based donations) */}
                        {donation.type === 'request' && (
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                            <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-3">
                              <Users className="w-5 h-5 text-purple-600" />
                              Requester Details
                            </h5>
                            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-purple-200">
                              <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                                  <Droplet className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">Blood Group {donation.bloodGroup}</h4>
                                  <p className="text-sm text-gray-600">{donation.requesterDetails?.name || 'Not available'}</p>
                                </div>
                                <div className="text-right">
                                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                                    {donation.amount ? `${donation.amount} ml` : '1 unit'}
                                  </span>
                                </div>
                              </div>

                              <div className="space-y-3 text-sm">
                                <div className="flex items-start gap-3">
                                  <Calendar className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-600">Created: {formatDate(donation.rawData?.request?.createdAt || donation.date)}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <p className="text-gray-600">
                                        Accepted on {formatDate(donation.rawData?.acceptedAt || donation.date)}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3">
                                  <Calendar className="w-5 h-5 text-purple-500 flex-shrink-0" />
                                  <p className="text-gray-600">
                                    Donation Date: {formatDate(donation.date).split(',')[0]}
                                  </p>
                                </div>

                                <div className="flex items-start gap-3">
                                  <MapPin className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                                  <p className="text-gray-600">
                                    {donation.rawData?.request?.address || donation.location || '3852, Ward No 5, Dhani Shyam Lal, Rajeev nagar, Hisar, Haryana 125001, India'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredDonations.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Gift className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">No Donation History Found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {bloodGroupFilter || timeFilter
              ? 'No donations match your current filters. Try adjusting your filters to see more results.'
              : 'Your blood donation history will appear here once you make your first donation. Start your lifesaving journey today!'}
          </p>
          {!bloodGroupFilter && !timeFilter && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-semibold shadow-lg"
            >
              Start Donating
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredDonations.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">1</span> to{" "}
              <span className="font-semibold text-gray-900">{filteredDonations.length}</span> of{" "}
              <span className="font-semibold text-gray-900">{filteredDonations.length}</span> donations
            </p>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={true}
                className="px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Previous
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={true}
                className="px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Next
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Preview Modal */}
      {showCertificatePreview && certificatePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Donation Certificate Preview
                </h3>
                <button
                  onClick={() => {
                    setShowCertificatePreview(false);
                    setCertificatePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex justify-center mb-6">
                <img
                  src={certificatePreview}
                  alt="Certificate Preview"
                  className="max-w-full h-auto rounded-lg border-2 border-gray-200 shadow-lg"
                />
              </div>

              <div className="flex gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowCertificatePreview(false);
                    setCertificatePreview(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  Close Preview
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    // Download the preview image
                    const link = document.createElement('a');
                    link.href = certificatePreview;
                    link.download = `donation_certificate_preview.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setShowCertificatePreview(false);
                    setCertificatePreview(null);
                    toast.success('Certificate downloaded successfully! 🎉');
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg"
                >
                  <Award className="w-5 h-5 inline mr-2" />
                  Download Certificate
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DonationHistory;